import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  accessScope,
  type Action,
  type FeatureFlag,
  type LoginInput,
  type Me,
  type Module,
} from '@repo/shared';
import { api } from '../lib/api';
import { clearOffline, rememberMe } from '../lib/offline';

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: async () => {
    const me = await api<Me>('/me');
    rememberMe(me);
    return me;
  },
  // Rights can change at any time (permission editor): re-read them when the user comes back to the tab.
  staleTime: 60_000,
  refetchOnWindowFocus: true,
  retry: false,
});

export function useMe(): Me {
  const { data } = useQuery(meQuery);
  if (!data) throw new Error('useMe used outside an authenticated route');
  return data;
}

/**
 * Back-office actions need the full scope; « own » (a teacher's sheets and sessions, a student's record) only
 * counts when the caller says so — otherwise a teacher would see « Nouvel étudiant » that the API refuses.
 */
export function useCan(module: Module, action: Action = 'read', allowOwn = false): boolean {
  const scope = accessScope(useMe().permissions, module, action);
  return scope === 'all' || (allowOwn && scope === 'own');
}

export function useFeature(flag: FeatureFlag): boolean {
  return useMe().features[flag];
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => api<Me>('/auth/login', { method: 'POST', body: input }),
    onSuccess: (me) => {
      qc.clear();
      clearOffline();
      rememberMe(me);
      qc.setQueryData(meQuery.queryKey, me);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ ok: true }>('/auth/logout', { method: 'POST' }),
    onSettled: () => {
      qc.clear();
      clearOffline();
    },
  });
}

/** Landing page per role after sign-in. */
export function homePathFor(me: Me): string {
  if (me.roles.includes('SUPER_ADMIN')) return '/platform';
  if (me.roles.includes('ETUDIANT')) return '/etudiant';
  if (me.roles.includes('CANDIDAT')) return '/candidature';
  if (me.roles.includes('ENSEIGNANT') && me.permissions.dashboard === 'none') return '/notes';
  return '/';
}
